import { CommonModule } from '@angular/common'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Component, DestroyRef, inject, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { forkJoin, Observable } from 'rxjs'
import { catchError, finalize, map } from 'rxjs/operators'
import { ToastrService } from '../../../../../../services/toastr.service'

interface ShortenedUrl {
  original: string
  shortened: string
  isCopied?: boolean
  expiryDate?: string
}

@Component({
  selector: 'ngx-url-shortener',
  templateUrl: './url-shortener.component.html',
  styleUrls: ['./url-shortener.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class UrlShortenerComponent implements OnInit {
  urlInput: string = ''
  expiryDate: string = ''
  showExpiryPicker: boolean = false
  results: ShortenedUrl[] = []
  allCopied: boolean = false
  error: string = ''
  isLoading: boolean = false
  minDate: Date = new Date()

  // Debugging getter to check button state
  get isButtonDisabled(): boolean {
    const disabled = !this.urlInput.trim() || this.isLoading
    return disabled
  }

  private readonly API_TOKEN = 'BrlgzLdZguI52O6HCLjmGG3t6PnYnFATst5XQkhsqVmF837VjPPhUBYaozTjcmIL'
  private readonly API_URL = 'https://mt.events/shorten'
  private readonly _destroyRef = inject(DestroyRef)

  constructor(private http: HttpClient, private toastr: ToastrService) {}

  ngOnInit(): void {
    // Set default expiry date to one year from current date
    const defaultExpiry = new Date()
    defaultExpiry.setFullYear(defaultExpiry.getFullYear() + 1)
    this.expiryDate = defaultExpiry.toISOString().split('T')[0]
  }

  /**
   * Clears the URL input field
   */
  clearInput(): void {
    this.urlInput = ''
  }

  /**
   * Validates if a string is a properly formatted URL
   */
  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  /**
   * Validates a comma-separated list of URLs
   */
  private validateUrls(input: string): { urls: string[]; error?: string } {
    const parts = input
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
    const urls: string[] = []
    const invalidUrls: string[] = []

    for (const part of parts) {
      if (this.isValidUrl(part)) {
        urls.push(part)
      } else {
        invalidUrls.push(part)
      }
    }

    if (invalidUrls.length > 0) {
      return {
        urls: [],
        error: `Invalid URLs found: ${invalidUrls.join(', ')}. Please ensure each URL starts with http:// or https:// and is properly separated by commas.`
      }
    }

    if (urls.length === 0) {
      return {
        urls: [],
        error: 'No valid URLs found. Please enter URLs starting with http:// or https:// and separated by commas.'
      }
    }

    return { urls }
  }

  /**
   * Shortens a single URL using the MT.events API
   */
  private shortenUrl(longUrl: string): Observable<string> {
    const headers = new HttpHeaders().set('token', this.API_TOKEN).set('Content-Type', 'application/json')

    const body = {
      long_url: longUrl,
      ...(this.expiryDate && { expiry_date: this.expiryDate })
    }

    return this.http.post<{ url: string; expiry_date: string; original_url: string }>(this.API_URL, body, { headers }).pipe(
      map((response) => {
        if (!response?.url) {
          throw new Error('Failed to shorten URL')
        }
        this.results.push({
          original: longUrl,
          shortened: response.url
        })
        return response.url
      }),
      catchError((error) => {
        console.error('Error shortening URL:', error)
        if (error.status === 401) {
          throw new Error('Authentication failed. Please check your API token.')
        }
        throw new Error('Error shortening URL')
      })
    )
  }

  /**
   * Handles the URL shortening process with loading state
   */
  handleShorten(): void {
    if (!this.urlInput.trim()) return

    this.isLoading = true
    this.error = ''

    const validation = this.validateUrls(this.urlInput)
    if (validation.error) {
      this.error = validation.error
      this.isLoading = false
      return
    }

    const shortenRequests = validation.urls.map((url) => this.shortenUrl(url))

    forkJoin(shortenRequests)
      .pipe(
        finalize(() => {
          this.isLoading = false
          this.allCopied = false
          this.urlInput = ''
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {},
        error: (error) => (this.error = error.message || 'Failed to shorten URLs')
      })
  }

  /**
   * Handles input changes for debugging
   */
  onInputChange(event: any): void {}

  /**
   * Handles keyboard shortcuts for URL shortening
   */
  onUrlInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      event.stopPropagation()

      if (this.urlInput.trim() && !this.isLoading) {
        this.handleShorten()
      }
    }
  }

  /**
   * Gets whether the current platform is Mac
   */
  get isMacPlatform(): boolean {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0
  }

  /**
   * Gets the primary keyboard modifier key for the current platform
   */
  get keyboardModifier(): string {
    return this.isMacPlatform ? 'Cmd' : 'Ctrl'
  }

  /**
   * Copies a single shortened URL to clipboard
   */
  async copyToClipboard(text: string, index: number): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
      this.results[index].isCopied = true
      setTimeout(() => {
        this.results[index].isCopied = false
      }, 2000)
    } catch (error) {
      this.toastr.error('Error', 'Failed to copy to clipboard')
    }
  }

  /**
   * Copies all shortened URLs to clipboard as newline-separated text
   */
  async copyAllToClipboard(): Promise<void> {
    const allUrls = this.results.map((r) => r.shortened).join('\n')
    try {
      await navigator.clipboard.writeText(allUrls)
      this.allCopied = true
      setTimeout(() => {
        this.allCopied = false
      }, 2000)
    } catch (error) {
      this.toastr.error('Error', 'Failed to copy to clipboard')
    }
  }

  /**
   * Clears all shortened URL results
   */
  clearResults(): void {
    this.results = []
    this.allCopied = false
  }

  /**
   * Filter function to disable dates before today
   */
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date >= today
  }
}
